import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { BodyLarge, BodyRegular, BodySmall, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { DropDownListItem, ReactSelectDropDownList } from '@shared/ui/organisms/drop-down-list/ReactSelectDropDownList';
import { UserSearchDropDownList } from '@shared/ui/molecules/UserSearchDropDownList';
import { BasicUserInfo } from '@shared/models/user.models';
import {
  assignLocker,
  deleteLocker,
  selectAllLockersForOffice,
  selectIsLockersLoading,
  selectIsLockersSaving,
  selectLockerSummary,
  selectLockerWaitingList,
  unassignLocker
} from '../data/lockers.duck';
import { Locker, LockerOfficeSummary, LockerStatusFilter, WaitingListEntry } from '../data/lockers.models';
import { formatDate, initialsFor } from '../data/lockers.utils';
import { LockerDialog } from './LockerDialog';
import { LockerRowMenu } from './LockerRowMenu';
import {
  CountPill,
  EmptyState,
  InitialsAvatar,
  LockerBodyLayout,
  LockerCard,
  LockerCardFooter,
  LockerCardHeader,
  LockerMainColumn,
  LockerPageBody,
  LockerRail,
  LockerTitleActions,
  LockerTitleRow,
  NarrowSubtitle,
  OfficeSelectWrapper,
  ProgressFill,
  ProgressTrack,
  RowAction,
  SearchField,
  SearchInput,
  Segment,
  SegmentedControl,
  StatCard,
  StatStrip,
  StatusDot,
  StatusPill,
  StatValue,
  TableCell,
  TableHeaderRow,
  TableRow,
  TableRowActions,
  TableToolbar,
  WideCell
} from './LockerViewComponents';

// Every track can shrink, so the table never pushes past the card it lives in.
const TABLE_COLUMNS = 'minmax(110px, 170px) minmax(96px, 150px) minmax(0, 1fr) minmax(96px, 170px) 190px';
const AVAILABLE_PILL_BACKGROUND = 'rgba(32, 204, 111, 0.12)';
const WAITING_PILL_BACKGROUND = 'rgba(240, 130, 34, 0.15)';

/**
 * Design 1a. One table rather than four tabs: an office admin's day is find a locker or a person,
 * then act on the row in front of them. The stat cards double as the filter, so the counts and the
 * list can never disagree about what "available" means.
 */
export function AdminLockerDirectory(props: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const lockers = useSelector(selectAllLockersForOffice);
  const summary = useSelector(selectLockerSummary);
  const waitingList = useSelector(selectLockerWaitingList);
  const isLoading = useSelector(selectIsLockersLoading);
  const isSaving = useSelector(selectIsLockersSaving);

  const [statusFilter, setStatusFilter] = useState<LockerStatusFilter>(LockerStatusFilter.All);
  const [searchTerm, setSearchTerm] = useState('');
  const [assigningLockerId, setAssigningLockerId] = useState<number | null>(null);
  const [isAddingLocker, setIsAddingLocker] = useState(false);
  const [editingLocker, setEditingLocker] = useState<Locker | null>(null);

  const availableCount = lockers.filter((locker: Locker) => locker.active && !locker.assigned).length;
  const assignedCount = lockers.filter((locker: Locker) => locker.assigned).length;
  const waitingCount = waitingList?.totalWaiting ?? 0;
  const waitingEntries = waitingList?.entries ?? [];

  // The rail is a glance at the front of the queue, so it is always in queue order.
  const topOfQueue = useMemo(() => {
    return [...waitingEntries]
      .sort((a: WaitingListEntry, b: WaitingListEntry) => a.position - b.position)
      .slice(0, 3);
  }, [waitingEntries]);

  const otherOffices = useMemo(() => {
    return summary.filter((office: LockerOfficeSummary) => office.officeId !== props.selectedOfficeId);
  }, [summary, props.selectedOfficeId]);

  const visibleLockers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return lockers.filter((locker: Locker) => {
      if (statusFilter === LockerStatusFilter.Available && (locker.assigned || !locker.active)) return false;
      if (statusFilter === LockerStatusFilter.Assigned && !locker.assigned) return false;
      if (!query) return true;

      // Searching a person only works here because assignee names are admin-only data.
      return locker.label.toLowerCase().includes(query)
        || (locker.assignedUserName ?? '').toLowerCase().includes(query);
    });
  }, [lockers, statusFilter, searchTerm]);

  const onUserSelected = (locker: Locker, user: BasicUserInfo) => {
    dispatch(assignLocker({ lockerId: locker.id, userId: user.userId }));
    setAssigningLockerId(null);
  };

  const onDeleteLocker = (locker: Locker) => {
    if (!window.confirm(t('lockers.confirm-delete', { label: locker.label }))) return;
    dispatch(deleteLocker(locker.id));
  };

  const goToWaitingList = () => history.push('/whereabouts/company/lockers/waiting-list');

  const subtitle = [
    t('lockers.locker-count-at-office', { count: lockers.length, office: props.selectedOfficeName }),
    t('lockers.free-count', { count: availableCount }),
    t('lockers.waiting-count', { count: waitingCount }),
  ].join(' · ');

  return (
    <LockerPageBody>
      <LockerTitleRow>
        <div>
          <HeadlineMedium style={{ marginBottom: 4 }}>{t('lockers.title')}</HeadlineMedium>
          <BodySmall colour={Colours.darkGrey}>{subtitle}</BodySmall>
        </div>

        <LockerTitleActions>
          <OfficeSelectWrapper>
            <BodySmall colour={Colours.darkGrey}>{t('lockers.office')}</BodySmall>
            <ReactSelectDropDownList items={props.officeOptions}
                                     selectedItem={props.selectedOfficeOption}
                                     placeholder={'lockers.select-office'}
                                     onItemSelected={(item: DropDownListItem) => props.selectOffice(Number(item.value))} />
          </OfficeSelectWrapper>

          <PrimaryButton text={'lockers.add-locker'} icon={IconTypes.Add} size={ButtonSize.Medium}
                         click={() => setIsAddingLocker(true)} />
        </LockerTitleActions>
      </LockerTitleRow>

      <StatStrip>
        {/* Three cards fit a phone; the segmented control below is how "all" is reached there. */}
        <StatCard type={'button'} hideNarrow={true} selected={statusFilter === LockerStatusFilter.All}
                  onClick={() => setStatusFilter(LockerStatusFilter.All)}>
          <BodySmall colour={Colours.darkGrey}>{t('lockers.all-lockers')}</BodySmall>
          <StatValue colour={Colours.blue}>{lockers.length}</StatValue>
        </StatCard>

        <StatCard type={'button'} selected={statusFilter === LockerStatusFilter.Available}
                  onClick={() => setStatusFilter(LockerStatusFilter.Available)}>
          <BodySmall colour={Colours.darkGrey}>{t('lockers.available')}</BodySmall>
          <StatValue colour={Colours.darkGreen}>{availableCount}</StatValue>
        </StatCard>

        <StatCard type={'button'} selected={statusFilter === LockerStatusFilter.Assigned}
                  onClick={() => setStatusFilter(LockerStatusFilter.Assigned)}>
          <BodySmall colour={Colours.darkGrey}>{t('lockers.assigned')}</BodySmall>
          <StatValue>{assignedCount}</StatValue>
        </StatCard>

        <StatCard type={'button'} onClick={goToWaitingList}>
          <BodySmall colour={Colours.darkGrey}>{t('lockers.waiting-list')}</BodySmall>
          <StatValue colour={Colours.orange}>{waitingCount}</StatValue>
        </StatCard>
      </StatStrip>

      {(isAddingLocker || !!editingLocker) && !!props.selectedOfficeId &&
        <LockerDialog rootOfficeId={props.selectedOfficeId}
                      locker={editingLocker ?? undefined}
                      onClose={() => {
                        setIsAddingLocker(false);
                        setEditingLocker(null);
                      }} />}

      <LockerBodyLayout>
        <LockerMainColumn>
          <LockerCard>
            <TableToolbar>
              <SearchField>
                <Icon icon={IconTypes.Search} style={{ color: Colours.darkGrey }} />
                <SearchInput value={searchTerm}
                             onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                             placeholder={t('lockers.search-locker-or-person')}
                             aria-label={t('lockers.search-locker-or-person')} />
              </SearchField>

              <SegmentedControl>
                {[LockerStatusFilter.All, LockerStatusFilter.Available, LockerStatusFilter.Assigned]
                  .map((filter: LockerStatusFilter) => (
                    <Segment key={filter} type={'button'} selected={statusFilter === filter}
                             onClick={() => setStatusFilter(filter)}>
                      {t(`lockers.filter-${filter}`)}
                    </Segment>
                  ))}
              </SegmentedControl>
            </TableToolbar>

            <TableHeaderRow columns={TABLE_COLUMNS}>
              <BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('lockers.locker')}</BodyVerySmall>
              <BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('lockers.status')}</BodyVerySmall>
              <BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('lockers.assigned-to')}</BodyVerySmall>
              <BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('lockers.assigned-on')}</BodyVerySmall>
              <BodyVerySmall weight={600} colour={Colours.darkGrey}
                             style={{ textAlign: 'right' }}>{t('lockers.actions')}</BodyVerySmall>
            </TableHeaderRow>

            {isLoading && <SimpleSpinner size={40} />}

            {!isLoading && visibleLockers.length === 0 && (
              <EmptyState>
                <BodyRegular colour={Colours.darkGrey}>
                  {lockers.length === 0 ? t('lockers.no-lockers-at-office') : t('lockers.no-matching-lockers')}
                </BodyRegular>
                {lockers.length > 0 && !!searchTerm &&
                  <RowAction width={140} style={{ margin: '12px auto 0' }}>
                    <OutlineButton text={'lockers.clear-search'} size={ButtonSize.Small}
                                   click={() => setSearchTerm('')} />
                  </RowAction>}
              </EmptyState>
            )}

            {!isLoading && visibleLockers.map((locker: Locker) => (
              <TableRow key={locker.id} columns={TABLE_COLUMNS}>
                <TableCell>
                  <Icon icon={IconTypes.Padlock} style={{ color: Colours.mildGrey }} />
                  <div style={{ minWidth: 0 }}>
                    <BodyRegular weight={600}>{locker.label}</BodyRegular>
                    <NarrowSubtitle>
                      <BodySmall colour={Colours.darkGrey}>
                        {locker.assigned
                          ? [locker.assignedUserName, formatDate(locker.assignedAt)].filter(Boolean).join(' · ')
                          : t('lockers.available')}
                      </BodySmall>
                    </NarrowSubtitle>
                  </div>
                </TableCell>

                <WideCell>
                  <StatusPill backgroundColour={locker.assigned ? Colours.lightGrey : AVAILABLE_PILL_BACKGROUND}>
                    <StatusDot colour={locker.assigned ? Colours.darkGrey : Colours.darkGreen} />
                    <BodyVerySmall weight={600} colour={locker.assigned ? Colours.darkGrey : Colours.darkGreen}>
                      {locker.assigned ? t('lockers.assigned') : t('lockers.available')}
                    </BodyVerySmall>
                  </StatusPill>
                </WideCell>

                <WideCell>
                  {locker.assigned
                    ? <>
                        <InitialsAvatar>{initialsFor(locker.assignedUserName)}</InitialsAvatar>
                        <BodyRegular>{locker.assignedUserName}</BodyRegular>
                      </>
                    : <BodyRegular colour={Colours.mildGrey}>&mdash;</BodyRegular>}
                </WideCell>

                <WideCell>
                  <BodySmall colour={Colours.darkGrey}>{formatDate(locker.assignedAt) || '—'}</BodySmall>
                </WideCell>

                {assigningLockerId === locker.id
                  ? <div style={{ minWidth: 260 }}>
                      <UserSearchDropDownList closeOnSelect={true}
                                              disabled={isSaving}
                                              onSelect={(user: BasicUserInfo) => onUserSelected(locker, user)} />
                    </div>
                  : <TableRowActions>
                      <RowAction>
                        {locker.assigned
                          ? <OutlineButton text={'lockers.release'} size={ButtonSize.Small} disabled={isSaving}
                                           click={() => dispatch(unassignLocker(locker.id))} />
                          : <OutlineButton text={'lockers.assign'} size={ButtonSize.Small} disabled={isSaving}
                                           click={() => setAssigningLockerId(locker.id)} />}
                      </RowAction>
                      <LockerRowMenu disabled={isSaving}
                                     onEdit={() => setEditingLocker(locker)}
                                     onDelete={() => onDeleteLocker(locker)} />
                    </TableRowActions>}
              </TableRow>
            ))}
          </LockerCard>
        </LockerMainColumn>

        <LockerRail>
          <LockerCard>
            {/* The rail only shows the top three, so the way to the rest is an explicit button. */}
            <LockerCardHeader>
              <RowAction width={170}>
                <OutlineButton text={'lockers.view-waiting-list'} size={ButtonSize.Small}
                               click={goToWaitingList} />
              </RowAction>
              <CountPill backgroundColour={WAITING_PILL_BACKGROUND}>
                <BodyVerySmall weight={700} colour={Colours.orange}>{waitingCount}</BodyVerySmall>
              </CountPill>
            </LockerCardHeader>

            {waitingEntries.length === 0
              ? <EmptyState>
                  <BodyRegular colour={Colours.darkGrey}>{t('lockers.waiting-list-empty')}</BodyRegular>
                </EmptyState>
              : topOfQueue.map((entry: WaitingListEntry) => (
                  <TableRow key={entry.id} columns={'1fr'}>
                    <TableCell>
                      <BodyRegular weight={700} colour={Colours.mildGrey}>{entry.position}</BodyRegular>
                      <div style={{ minWidth: 0 }}>
                        <BodyRegular weight={600}>{entry.userName}</BodyRegular>
                        <BodyVerySmall colour={Colours.darkGrey}>
                          {t('lockers.joined-on')} {formatDate(entry.createdAt)}
                        </BodyVerySmall>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

            {waitingCount > 0 && (
              <LockerCardFooter>
                <BodySmall colour={Colours.darkGrey}>
                  {t('lockers.queue-clearing-summary', {
                    free: availableCount,
                    assignments: Math.min(availableCount, waitingCount),
                  })}
                </BodySmall>
              </LockerCardFooter>
            )}
          </LockerCard>

          {otherOffices.length > 0 && (
            <LockerCard>
              <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <BodyLarge weight={600}>{t('lockers.other-offices')}</BodyLarge>

                {otherOffices.map((office: LockerOfficeSummary) => (
                  <div key={office.officeId}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                      <BodyRegular>{office.officeName}</BodyRegular>
                      <BodySmall colour={Colours.darkGrey}>
                        {office.assignedLockers} / {office.totalLockers}
                      </BodySmall>
                    </div>
                    <ProgressTrack>
                      <ProgressFill percent={office.totalLockers === 0
                        ? 0
                        : (office.assignedLockers / office.totalLockers) * 100} />
                    </ProgressTrack>
                  </div>
                ))}
              </div>
            </LockerCard>
          )}
        </LockerRail>
      </LockerBodyLayout>
    </LockerPageBody>
  );
}

interface Props {
  officeOptions: DropDownListItem[];
  selectedOfficeOption?: DropDownListItem;
  selectedOfficeId: number | null;
  selectedOfficeName: string;
  selectOffice: (officeId: number) => void;
}
